<template>
  <modal
    title="Merge People"
    ref="modal"
  >
    <div v-if="persons.length">
      <div class="text-subtitle2 q-mb-sm">Select target person</div>
      <div class="text-caption text-grey-6 q-mb-md">
        All faces from other selected persons will be moved to the target.
        The other persons will be deleted.
      </div>

      <q-list
        bordered
        separator
        class="rounded-borders"
      >
        <q-item
          v-for="person in persons"
          :key="person.id"
          clickable
          :active="targetPersonId === person.id"
          @click="targetPersonId = person.id"
        >
          <q-item-section avatar>
            <q-avatar
              size="40px"
              color="grey-3"
              text-color="grey-6"
            >
              <q-img
                v-if="person.primaryFace && person.primaryFace.imageUrl"
                :src="person.primaryFace.imageUrl"
                fit="cover"
              />
              <q-icon
                v-else
                name="fa fa-user"
              />
            </q-avatar>
          </q-item-section>
          <q-item-section>
            <q-item-label>{{ person.label || 'Unnamed Person' }}</q-item-label>
            <q-item-label caption>{{ person.faceCount || 0 }} faces</q-item-label>
          </q-item-section>
          <q-item-section side>
            <q-radio
              v-model="targetPersonId"
              :val="person.id"
            />
          </q-item-section>
        </q-item>
      </q-list>
    </div>

    <template v-slot:actions>
      <q-btn
        flat
        label="Cancel"
        @click="hide()"
      />
      <async-btn
        label="Merge"
        color="primary"
        :task="task"
        :disabled="!canMerge"
        @click="merge"
      />
    </template>
  </modal>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { HasTaskMixin, ModalMixin } from '@/mixins';
import { Person } from '@/analysis/types';
import { Modal as ModalElement } from '@/type';
import Modal from '@/components/modal/Modal.vue';
import AsyncBtn from '@/components/AsyncBtn.vue';
import notify from '@/components/notify';
import { merge } from './person';

interface Data {
  persons: Person[];
  targetPersonId: number | null;
}

export default defineComponent({
  emits: ['merged'],
  mixins: [HasTaskMixin, ModalMixin],
  components: {
    Modal,
    AsyncBtn,
  },
  data(): Data {
    return {
      persons: [],
      targetPersonId: null,
    };
  },
  computed: {
    canMerge(): boolean {
      return this.targetPersonId !== null && this.persons.length >= 2;
    },
    sourcePersonIds(): number[] {
      if (!this.targetPersonId) return [];
      return this.persons
        .filter(p => p.id !== this.targetPersonId)
        .map(p => p.id);
    },
  },
  methods: {
    open(persons: Person[]) {
      this.persons = [...persons];
      this.targetPersonId = persons.length > 0 ? persons[0].id : null;
      this.task = null;
      (this.$refs.modal as ModalElement).show();
    },
    merge() {
      if (!this.canMerge) return;

      this.task = merge({
        targetPersonId: this.targetPersonId!,
        sourcePersonIds: this.sourcePersonIds,
      });
      this.task.then((person: Person) => {
        this.$emit('merged', {
          target: person,
          deletedIds: this.sourcePersonIds,
        });
        (this.$refs.modal as ModalElement).hide();
        notify('Persons merged.');
      });
    },
  },
});
</script>
